/**
 * Lykke in the Video studio sidebar — the Deck studio pattern: say what you want, Lykke changes the video with tools
 * (the preview updates as she works), LOOKS at the keyframes before she answers, and replies in a sentence or two.
 * The frames she looked at are shown under her reply, so the person sees what she saw.
 */
import { useEffect, useRef, useState } from 'react';
import { studioErrorText } from '@/cowork/launch/studioErrors';
import { ArrowUp, Loader2, Sparkle, Square } from 'lucide-react';
import { coworkApi, type VideoChatEvent, type VideoChatTurn } from '@/cowork/omniad/api/coworkApi';
import { Said } from '@/cowork/reports/DeckChat';
import type { VideoSpec } from '@/video/spec';
import { KeyframeSheet } from './KeyframeSheet';

const STARTERS = ['Make the hook stop the scroll', 'Cut it to 15 seconds', 'Find a stronger clip for the middle', 'Make it feel more premium'];

export function VideoChat({ videoId, disabled, onPlan, onRunStart, onRunEnd, describeChange, sceneCount, portrait, onPickScene }: {
  videoId: string;
  /** What the run changed, in words ("scene 1 (hook) · format 4:5"), read when Lykke finishes. */
  describeChange?: () => string | null;
  sceneCount: number;
  portrait: boolean;
  onPickScene?: (index: number) => void;
  /** While the first plan is written, chat waits. */
  disabled?: boolean;
  onPlan: (spec: VideoSpec, issues: string[]) => void;
  onRunStart?: () => void;
  onRunEnd?: (changed: boolean) => void;
}) {
  const [turns, setTurns] = useState<Array<VideoChatTurn & { changed?: string }>>([]);
  const [draft, setDraft] = useState('');
  const [running, setRunning] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [lookNow, setLookNow] = useState<string | null>(null);
  const abort = useRef<AbortController | null>(null);
  const end = useRef<HTMLDivElement>(null);

  useEffect(() => { void coworkApi.videos.chatHistory(videoId).then((r) => setTurns(r.turns)).catch(() => undefined); }, [videoId]);
  useEffect(() => { end.current?.scrollIntoView?.({ block: 'end' }); }, [turns, status, lookNow]);

  async function send(text: string) {
    const message = text.trim();
    if (!message || running) return;
    setDraft('');
    setTurns((t) => [...t, { role: 'user', text: message }]);
    setRunning(true); setStatus('Thinking'); setLookNow(null);
    onRunStart?.();
    const ctl = new AbortController(); abort.current = ctl;
    let changed = false;
    try {
      await coworkApi.videos.chat(videoId, message, (ev: VideoChatEvent) => {
        if (ev.event === 'status') setStatus(ev.label);
        else if (ev.event === 'plan') { changed = true; onPlan(ev.spec, ev.issues); }
        else if (ev.event === 'look') setLookNow(ev.url);
        else if (ev.event === 'done') { const note = ev.changed ? describeChange?.() : null; setTurns((t) => [...t, { role: 'assistant', text: ev.reply, look: ev.look || undefined, ...(note ? { changed: note } : {}) }]); setLookNow(null); }
      }, ctl.signal);
    } catch (e) {
      if (!ctl.signal.aborted) setTurns((t) => [...t, { role: 'assistant', text: studioErrorText(e, 'video') }]);
    } finally {
      setRunning(false); setStatus(null); abort.current = null;
      onRunEnd?.(changed);
    }
  }

  function stop() {
    void coworkApi.videos.chatStop(videoId).catch(() => undefined);
    abort.current?.abort();
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto pb-3 text-[13.5px] leading-relaxed text-[#292524]">
        {!turns.length && !running && (
          <div className="flex flex-col gap-2.5 rounded-xl bg-[#faf7fb] p-3.5">
            <span className="flex items-center gap-1.5 text-[13px] font-semibold text-[#2e024c]"><Sparkle className="h-4 w-4 text-[#be43ff]" />Lykke</span>
            <p className="text-[12.5px] text-[#57534e]">Tell me what to change: the hook, the pace, a clip, the language. I change the video, look at every scene, then tell you what I did.</p>
            <div className="flex flex-wrap gap-1.5">
              {STARTERS.map((s) => <button key={s} type="button" disabled={disabled} onClick={() => void send(s)} className="rounded-full border border-[#e7e5e4] bg-white px-2.5 py-1 text-[12px] text-[#44403c] hover:border-[#be43ff] disabled:opacity-40">{s}</button>)}
            </div>
          </div>
        )}
        {turns.map((t, i) => (
          t.role === 'user'
            ? <div key={i} className="ml-8 self-end rounded-2xl rounded-br-md bg-[#2e024c] px-3.5 py-2 text-[13px] text-white">{t.text}</div>
            : (
              <div key={i} className="flex flex-col gap-2">
                <Said text={t.text} />
                {(t as { changed?: string }).changed && <p className="rounded-lg bg-[#f4ebff] px-2.5 py-1.5 text-[12px] text-[#53389e]"><b>Changed:</b> {(t as { changed?: string }).changed}</p>}
                {t.look && <KeyframeSheet url={t.look} count={sceneCount} portrait={portrait} onPick={onPickScene} className="w-full rounded-lg border border-[#eaecf0]" />}
              </div>
            )
        ))}
        {running && (
          <div className="flex flex-col gap-2">
            <span className="flex items-center gap-2 text-[12.5px] text-[#78716c]"><Loader2 className="h-3.5 w-3.5 animate-spin text-[#be43ff]" />{status || 'Working'}…</span>
            {lookNow && <KeyframeSheet url={lookNow} count={sceneCount} portrait={portrait} className="w-full rounded-lg border border-[#eaecf0] opacity-90" />}
          </div>
        )}
        <div ref={end} />
      </div>
      <div className="flex items-end gap-2 rounded-2xl border border-[#d6d3d1] bg-white p-2 focus-within:border-[#be43ff] focus-within:ring-2 focus-within:ring-[#f4ebff]">
        <textarea
          value={draft}
          disabled={disabled}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(draft); } }}
          rows={2}
          placeholder={disabled ? 'Lykke is writing the first version…' : 'Ask Lykke to change the video'}
          className="max-h-40 min-h-[44px] flex-1 resize-none bg-transparent px-1.5 py-1 text-[13.5px] text-[#1c1917] outline-none placeholder:text-[#a8a29e]"
        />
        {running
          ? <button type="button" onClick={stop} aria-label="Stop" className="grid h-8 w-8 place-items-center rounded-full bg-[#1c1917] text-white"><Square className="h-3.5 w-3.5" /></button>
          : <button type="button" disabled={disabled || !draft.trim()} onClick={() => void send(draft)} aria-label="Send" className="grid h-8 w-8 place-items-center rounded-full bg-[#2e024c] text-white disabled:opacity-30"><ArrowUp className="h-4 w-4" /></button>}
      </div>
    </div>
  );
}
